/**
 * DIPS2.0 登録機体一覧ページ
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { dipsApi } from '../services/api';
import { Icon } from '../components/icons';
import { Button, Card, Badge, Alert, Modal, Drawer } from '../components/ui';

// 機体種類アイコン (サンプルからのインライン SVG、Tabler 系)
// aircraft_type: '1' マルチローター / '2' ヘリコプター / '3' 固定翼 / '4' 固定翼VTOL
function AircraftTypeIcon({ type, size = 18, className = '' }) {
  const baseProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className: `inline-block ${className}`,
  };
  switch (String(type)) {
    case '1':
      // Multi-Rotor: 中央円 + 4 方向プロペラ
      return (
        <svg {...baseProps}>
          <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
          <path d="M12 9l0 -5" />
          <path d="M8 4l8 0" />
          <path d="M12 15l0 5" />
          <path d="M8 20l8 0" />
          <path d="M15 12l5 0" />
          <path d="M20 8l0 8" />
          <path d="M9 12l-5 0" />
          <path d="M4 8l0 8" />
          <circle cx="12" cy="12" r="1.5" />
        </svg>
      );
    case '2':
      // Helicopter: プロペラ付き胴体
      return (
        <svg {...baseProps}>
          <path d="M3 10l1 2h6l3 5h6a3 3 0 0 0 3 -3v-2l-5 -2z" />
          <path d="M12 10v-3" />
          <path d="M7 7h10" />
          <path d="M12 17v3" />
          <path d="M8 20h8" />
          <path d="M3 10v-1" />
        </svg>
      );
    case '3':
      // Fixed-Wing: 矢印型
      return (
        <svg {...baseProps}>
          <path d="M16 10h4a2 2 0 0 1 0 4h-4l-4 7h-3l2 -7h-4l-2 2h-3l2 -4l-2 -4h3l2 2h4l-2 -7h3z" />
        </svg>
      );
    case '4':
      // Fixed-Wing VTOL: 矢印 + 2 つの circle (回転翼)
      // 左 90 度回転で上向きに
      return (
        <svg {...baseProps}>
          <g transform="rotate(-90 12 12)">
            <path d="M16 10h4a2 2 0 0 1 0 4h-4l-4 7h-3l2 -7h-4l-2 2h-3l2 -4l-2 -4h3l2 2h4l-2 -7h3z" />
            <circle cx="14" cy="3" r="1.5" />
            <circle cx="13" cy="21" r="1.5" />
          </g>
        </svg>
      );
    default:
      // 不明: ? マーク付き円
      return (
        <svg {...baseProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 17l0 .01" />
          <path d="M12 13.5a1.5 1.5 0 0 1 1 -1.5a2.6 2.6 0 1 0 -3 -4" />
        </svg>
      );
  }
}

const MaterialIcon = ({ name, className = '', filled = false, weight, style = {} }) => {
  const fillSetting = filled ? "'FILL' 1" : "'FILL' 0";
  const wghtSetting = weight ? `, 'wght' ${weight}` : '';
  return (
    <span
      className={`material-symbols-outlined ${className}`}
      style={{ fontVariationSettings: `${fillSetting}${wghtSetting}`, ...style }}
    >
      {name}
    </span>
  );
};

// 機体種類のラベル
const AIRCRAFT_TYPE_LABELS = {
  '1': '回転翼（マルチローター）',
  '2': '回転翼（ヘリコプター）',
  '3': '固定翼',
  '4': '固定翼（VTOL）',
};

// 所有者区分のラベル (DIPS の値表記揺れに対応: 文字列 / 数値 / '2' 旧表記等)
const OWNER_CLASSIFICATION_LABELS = {
  '1': '個人',
  '2': '法人',
  '9': '法人',
};
const classificationLabel = (v) =>
  OWNER_CLASSIFICATION_LABELS[String(v ?? '')] || v;

// ヘルパー関数: ネストされたデータを取得
const getAircraftInfo = (aircraft) => aircraft?.aircraft_information || aircraft;
const getOwnerInfo = (aircraft) => aircraft?.owner_information;
const getUserInfo = (aircraft) => aircraft?.user_information;

// 日付フォーマット
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('ja-JP');
  } catch {
    return dateStr;
  }
};

// 機体詳細ドロワー内のタブ
const AIRCRAFT_DETAIL_TABS = [
  { key: 'basic', label: '基本情報' },
  { key: 'rid', label: 'リモートID情報' },
  { key: 'spec', label: 'スペック' },
  { key: 'raw', label: '生データ (JSON)' },
];

// 所有者・使用者ドロワー内のタブ
const PARTY_DETAIL_TABS = [
  { key: 'detail', label: '詳細' },
  { key: 'raw', label: '生データ (JSON)' },
];

function TabBar({ value, onChange, tabs }) {
  return (
    <div className="flex border-b border-border-light px-6">
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className={`relative px-4 py-3 text-sm font-medium transition-colors ${
              active ? 'text-nexus-primary' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.label}
            {active && (
              <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-nexus-primary rounded-t" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export default function DipsAircrafts() {
  const [dipsAircrafts, setDipsAircrafts] = useState(null);
  const [dipsLoading, setDipsLoading] = useState(false);
  const [dipsError, setDipsError] = useState(null);
  const [dipsAuthenticated, setDipsAuthenticated] = useState(dipsApi.isAuthenticated());
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [aircraftDetailTab, setAircraftDetailTab] = useState('basic');
  const [selectedParty, setSelectedParty] = useState(null); // { kind: 'owner'|'user', raw }
  const [partyDetailTab, setPartyDetailTab] = useState('detail');

  // 飛行計画
  const [flightPlans, setFlightPlans] = useState([]);
  const [flightPlansLoading, setFlightPlansLoading] = useState(false);
  const [flightPlansError, setFlightPlansError] = useState(null);
  const [selectedFlightPlan, setSelectedFlightPlan] = useState(null);
  const [flightPlanAircraft, setFlightPlanAircraft] = useState(null); // 飛行計画を表示中の機体
  const [flightPlanAuthenticated, setFlightPlanAuthenticated] = useState(dipsApi.isFlightPlanAuthenticated());
  const [flightPlanAuthLoading, setFlightPlanAuthLoading] = useState(false);
  const [searchDate, setSearchDate] = useState(() => {
    // デフォルトは今日
    const today = new Date();
    return today.toISOString().split('T')[0]; // "YYYY-MM-DD"
  });
  const [scanMode, setScanMode] = useState(false); // 範囲スキャンモード
  const [scanProgress, setScanProgress] = useState('');
  const [scanResults, setScanResults] = useState([]); // スキャン結果

  // 飛行計画登録フォーム
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportResult, setReportResult] = useState(null);
  const [reportForm, setReportForm] = useState({
    // 飛行日時
    flightDate: new Date().toISOString().split('T')[0],
    startTime: '09:00',
    endTime: '17:00',
    // 飛行エリア（緯度・経度）
    latitude: '35.6895',  // 東京
    longitude: '139.6917',
    radius: '500', // メートル
    // 飛行目的
    purpose: '空撮',
    // 高度
    maxAltitude: '150',
    // 備考
    remarks: '',
  });

  // DIPS機体情報取得
  const fetchDipsAircrafts = useCallback(async () => {
    if (!dipsApi.isAuthenticated()) {
      setDipsAuthenticated(false);
      return;
    }
    setDipsLoading(true);
    setDipsError(null);
    try {
      const data = await dipsApi.getAircrafts();
      setDipsAircrafts(data);
      setDipsAuthenticated(true);
    } catch (err) {
      setDipsError(err.message);
      if (err.message.includes('認証')) {
        setDipsAuthenticated(false);
      }
    } finally {
      setDipsLoading(false);
    }
  }, []);

  // 日付を "yyyyMMdd HHmm" 形式にフォーマット
  const formatDipsDateTime = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${y}${m}${d} ${h}${min}`;
  };

  // DIPS飛行計画取得（地理的範囲と日時で検索）
  const fetchFlightPlans = useCallback(async () => {
    console.log('[DIPS] fetchFlightPlans called, searchDate:', searchDate);
    console.log('[DIPS] isFlightPlanAuthenticated:', dipsApi.isFlightPlanAuthenticated());

    if (!dipsApi.isFlightPlanAuthenticated()) {
      console.log('[DIPS] Flight plan not authenticated, skipping');
      return;
    }

    setFlightPlansLoading(true);
    setFlightPlansError(null);
    try {
      // 選択した日付の00:00から23:59まで（24時間以内）
      const [year, month, day] = searchDate.split('-').map(Number);
      const startTime = new Date(year, month - 1, day, 0, 0);
      const finishTime = new Date(year, month - 1, day, 23, 59);

      // FPR APIの正しいリクエスト構造
      // features: 検索範囲（日本全体をカバーする広い円）
      // 東京を中心に半径500km
      const searchParams = {
        features: {
          type: 'Circle',
          center: [139.6917, 35.6895], // 東京
          radius: 500000, // 500km
        },
        startTime: formatDipsDateTime(startTime),
        finishTime: formatDipsDateTime(finishTime),
        allFlightPlan: '1', // 自分の飛行計画のみ
      };

      console.log('[DIPS] Flight plan search params:', searchParams);
      const data = await dipsApi.getFlightPlans(searchParams);
      console.log('[DIPS] Flight plans response:', data);

      // レスポンスから飛行計画リストを抽出
      const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];
      setFlightPlans(plans);
    } catch (err) {
      console.error('[DIPS] Flight plans error:', err);
      setFlightPlansError(err.message);
    } finally {
      setFlightPlansLoading(false);
    }
  }, [searchDate]);

  // DIPS認証開始
  const handleDipsAuth = async () => {
    console.log('[DIPS] handleDipsAuth called');
    setDipsLoading(true);
    setDipsError(null);
    try {
      console.log('[DIPS] Getting auth URL...');
      const result = await dipsApi.getAuthUrl('aircraft');
      console.log('[DIPS] Auth URL result:', result);
      const { authUrl } = result;

      if (!authUrl) {
        throw new Error('認証URLが取得できませんでした');
      }

      // postMessageイベントリスナーを設定
      const handleMessage = (event) => {
        console.log('[DIPS] Received postMessage:', event.data);
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          console.log('[DIPS] Received auth success via postMessage');
          dipsApi.storeToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setDipsAuthenticated(true);
          setDipsLoading(false);
          fetchDipsAircrafts();
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          console.error('[DIPS] Received auth error via postMessage:', event.data.error);
          // 認証エラー時は不整合状態を避けるため、token と画面状態をリセット
          dipsApi.clearToken();
          setDipsAuthenticated(false);
          setDipsAircrafts(null);
          // Keycloak 側のサーバーセッションもクリア
          const logoutWin = window.open(
            'https://www.dips-regdev.mlit.go.jp/auth/realms/drs-utm/protocol/openid-connect/logout',
            'dips_logout',
            'width=500,height=400'
          );
          if (logoutWin) setTimeout(() => logoutWin.close(), 500);
          setDipsError('認証エラー: ' + event.data.error);
          setDipsLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);
      console.log('[DIPS] postMessage listener added');

      console.log('[DIPS] Opening auth window:', authUrl);
      const authWindow = window.open(authUrl, 'dips_auth', 'width=600,height=700');

      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。ポップアップを許可してください。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            console.log('[DIPS] Auth window closed, checking token...');
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isAuthenticated()) {
                console.log('[DIPS] Token found after window close');
                setDipsAuthenticated(true);
                setDipsLoading(false);
                fetchDipsAircrafts();
              } else {
                console.log('[DIPS] No token found after window close');
                setDipsLoading(false);
              }
            }, 1000);
          }
        } catch {
          // Cross-origin エラーは無視
        }
      }, 500);
    } catch (err) {
      console.error('[DIPS] Auth error:', err);
      setDipsError('認証エラー: ' + err.message);
      setDipsLoading(false);
    }
  };

  // DIPS認証済みなら機体情報を自動取得
  useEffect(() => {
    if (dipsAuthenticated && !dipsAircrafts) {
      fetchDipsAircrafts();
    }
  }, [dipsAuthenticated, dipsAircrafts, fetchDipsAircrafts]);

  // 機体詳細を開いたらタブを basic に戻す
  useEffect(() => {
    if (selectedAircraft) setAircraftDetailTab('basic');
  }, [selectedAircraft]);

  // タイトルバー右の連携状態バッジ
  const { setHeaderBadge } = useOutletContext() || {};
  useEffect(() => {
    if (!setHeaderBadge) return undefined;
    setHeaderBadge(
      dipsAuthenticated
        ? { variant: 'success', label: '連携済み' }
        : { variant: 'danger', label: '未連携' }
    );
    return () => setHeaderBadge(null);
  }, [dipsAuthenticated, setHeaderBadge]);

  // 所有者/使用者詳細を開いたらタブを detail に戻す
  useEffect(() => {
    if (selectedParty) setPartyDetailTab('detail');
  }, [selectedParty]);

  // 認証完了時または日付変更時に飛行計画を取得
  useEffect(() => {
    console.log('[DIPS] useEffect check - flightPlanAuthenticated:', flightPlanAuthenticated, 'searchDate:', searchDate);
    if (flightPlanAuthenticated && !scanMode) {
      console.log('[DIPS] Calling fetchFlightPlans from useEffect');
      fetchFlightPlans();
    }
  }, [flightPlanAuthenticated, searchDate, scanMode, fetchFlightPlans]);

  // 範囲スキャン: 過去2週間 + 未来2ヶ月を検索
  const scanDateRange = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setScanMode(true);
    setScanResults([]);
    setFlightPlans([]);
    setFlightPlansError(null);

    const today = new Date();
    const results = [];

    // 過去2週間（14日）+ 未来2ヶ月（60日）= 約74日
    const startOffset = -14; // 2週間前
    const endOffset = 60;    // 2ヶ月後

    for (let i = startOffset; i <= endOffset; i++) {
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + i);

      const dateStr = targetDate.toISOString().split('T')[0];
      setScanProgress(`検索中: ${dateStr} (${i - startOffset + 1}/${endOffset - startOffset + 1})`);

      try {
        const startTime = new Date(targetDate);
        startTime.setHours(0, 0, 0, 0);
        const finishTime = new Date(targetDate);
        finishTime.setHours(23, 59, 0, 0);

        const searchParams = {
          features: {
            type: 'Circle',
            center: [139.6917, 35.6895],
            radius: 500000,
          },
          startTime: formatDipsDateTime(startTime),
          finishTime: formatDipsDateTime(finishTime),
          allFlightPlan: '1',
        };

        const data = await dipsApi.getFlightPlans(searchParams);
        const plans = Array.isArray(data) ? data : data.flight_plans || data.flightPlans || data.items || data.results || [];

        if (plans.length > 0) {
          results.push({
            date: dateStr,
            count: plans.length,
            plans: plans,
          });
          console.log(`[DIPS] Found ${plans.length} plans on ${dateStr}`);
        }
      } catch (err) {
        // エラーは無視して続行
        console.log(`[DIPS] Error on ${dateStr}:`, err.message);
      }

      // API負荷軽減のため少し待機
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setScanProgress('');
    setScanResults(results);
    setScanMode(false);

    console.log('[DIPS] Scan complete. Results:', results);
  };

  // 飛行計画表示用（機体選択は不要になったが互換性のため残す）
  const handleShowFlightPlans = (aircraft) => {
    console.log('[DIPS] handleShowFlightPlans called');
    setFlightPlanAircraft(aircraft);
  };

  // 飛行計画認証開始
  const handleFlightPlanAuth = async () => {
    console.log('[DIPS] handleFlightPlanAuth called');
    setFlightPlanAuthLoading(true);
    setFlightPlansError(null);
    try {
      const result = await dipsApi.getAuthUrl('flightPlan');
      const { authUrl } = result;

      if (!authUrl) {
        throw new Error('認証URLが取得できませんでした');
      }

      // postMessageイベントリスナーを設定
      const handleMessage = (event) => {
        console.log('[DIPS FlightPlan] Received postMessage:', event.data);
        if (event.data?.type === 'DIPS_AUTH_SUCCESS') {
          console.log('[DIPS FlightPlan] Received auth success via postMessage');
          dipsApi.storeFlightPlanToken({
            accessToken: event.data.accessToken,
            refreshToken: event.data.refreshToken,
            expiresIn: event.data.expiresIn,
          });
          setFlightPlanAuthenticated(true);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        } else if (event.data?.type === 'DIPS_AUTH_ERROR') {
          console.error('[DIPS FlightPlan] Received auth error:', event.data.error);
          // 認証エラー時は不整合状態を避けるため、token と画面状態をリセット
          dipsApi.clearFlightPlanToken();
          setFlightPlanAuthenticated(false);
          // Keycloak 側のサーバーセッションもクリア
          const logoutWin = window.open(
            'https://www.stg.uafp.dips.mlit.go.jp/auth/realms/drs-fpl/protocol/openid-connect/logout',
            'dips_flightplan_logout',
            'width=500,height=400'
          );
          if (logoutWin) setTimeout(() => logoutWin.close(), 500);
          setFlightPlansError('認証エラー: ' + event.data.error);
          setFlightPlanAuthLoading(false);
          window.removeEventListener('message', handleMessage);
        }
      };
      window.addEventListener('message', handleMessage);

      const authWindow = window.open(authUrl, 'dips_flightplan_auth', 'width=600,height=700');

      if (!authWindow) {
        window.removeEventListener('message', handleMessage);
        throw new Error('ポップアップがブロックされました。');
      }

      const checkAuth = setInterval(() => {
        try {
          if (authWindow?.closed) {
            clearInterval(checkAuth);
            setTimeout(() => {
              window.removeEventListener('message', handleMessage);
              if (dipsApi.isFlightPlanAuthenticated()) {
                setFlightPlanAuthenticated(true);
                setFlightPlanAuthLoading(false);
              } else {
                setFlightPlanAuthLoading(false);
              }
            }, 1000);
          }
        } catch {
          // Cross-origin エラーは無視
        }
      }, 500);
    } catch (err) {
      console.error('[DIPS FlightPlan] Auth error:', err);
      setFlightPlansError('認証エラー: ' + err.message);
      setFlightPlanAuthLoading(false);
    }
  };

  // 飛行計画通報（登録）
  const handleReportFlightPlan = async () => {
    if (!dipsApi.isFlightPlanAuthenticated()) {
      setFlightPlansError('飛行計画認証が必要です');
      return;
    }

    setReportLoading(true);
    setReportResult(null);
    setFlightPlansError(null);

    try {
      // 日時をDIPS形式に変換
      const [year, month, day] = reportForm.flightDate.split('-').map(Number);
      const [startH, startM] = reportForm.startTime.split(':').map(Number);
      const [endH, endM] = reportForm.endTime.split(':').map(Number);

      const startDateTime = new Date(year, month - 1, day, startH, startM);
      const endDateTime = new Date(year, month - 1, day, endH, endM);

      // FPR API リクエストボディ
      const reportData = {
        // 飛行エリア（円形）
        features: {
          type: 'Circle',
          center: [parseFloat(reportForm.longitude), parseFloat(reportForm.latitude)],
          radius: parseInt(reportForm.radius, 10),
        },
        // 飛行開始・終了時刻
        startTime: formatDipsDateTime(startDateTime),
        finishTime: formatDipsDateTime(endDateTime),
        // 飛行目的
        flightPurpose: reportForm.purpose,
        // 最大高度
        maxAltitude: parseInt(reportForm.maxAltitude, 10),
        // 備考
        remarks: reportForm.remarks || undefined,
      };

      console.log('[DIPS] Report flight plan:', reportData);
      const result = await dipsApi.reportFlightPlan(reportData);
      console.log('[DIPS] Report result:', result);

      setReportResult({ success: true, data: result });
      // 成功したら検索を再実行
      fetchFlightPlans();
    } catch (err) {
      console.error('[DIPS] Report error:', err);
      setReportResult({ success: false, error: err.message });
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* アクションボタン (常に表示、同期は未連携時 disabled) */}
      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={fetchDipsAircrafts}
          disabled={!dipsAuthenticated || dipsLoading}
        >
          <Icon name="refresh" className="w-4 h-4" />
          同期
        </Button>
        <Button
          variant="secondary"
          disabled={!dipsAuthenticated}
          onClick={() => {
            dipsApi.clearToken();
            setDipsAuthenticated(false);
            setDipsAircrafts(null);
            const logoutWindow = window.open(
              'https://www.dips-regdev.mlit.go.jp/auth/realms/drs-utm/protocol/openid-connect/logout',
              'dips_logout',
              'width=500,height=400'
            );
            if (logoutWindow) {
              setTimeout(() => {
                logoutWindow.close();
              }, 500);
            }
          }}
        >
          <Icon name="logout" className="w-4 h-4" />
          DIPS連携解除(ログアウト)
        </Button>
      </div>

      {/* エラー表示 */}
      {dipsError && (
        <Alert variant="error">{dipsError}</Alert>
      )}

      {/* 未認証状態（ローディング中は非表示） */}
      {!dipsAuthenticated && !dipsError && !dipsLoading && (
        <Card>
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-light-neutral rounded-full flex items-center justify-center">
              <Icon name="link" className="w-10 h-10 text-outline" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              DIPS2.0と連携する
            </h2>
            <p className="text-text-secondary mb-6 max-w-md mx-auto">
              国土交通省のドローン情報基盤システム（DIPS2.0）と連携すると、
              登録されている機体情報を表示できます。
            </p>
            <Button size="lg" onClick={handleDipsAuth} disabled={dipsLoading}>
              <Icon name="link" className="w-5 h-5" />
              DIPS2.0認証を開始
            </Button>
          </div>
        </Card>
      )}

      {/* ローディング */}
      {dipsLoading && (
        <Card>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-nexus-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-text-primary font-medium">機体情報を取得中...</p>
              <p className="text-text-secondary text-sm mt-1">DIPS2.0と通信しています</p>
            </div>
          </div>
        </Card>
      )}

      {/* 所有者・使用者情報（上部に1回だけ表示） */}
      {dipsAuthenticated && !dipsLoading && dipsAircrafts?.length > 0 && (() => {
        const firstAircraft = dipsAircrafts[0];
        const owner = getOwnerInfo(firstAircraft);
        const user = getUserInfo(firstAircraft);
        const isUserDifferent = user && user.owner_user_same_confirmation === '2';

        return (
          <div className="space-y-4">
            {/* 所有者情報 */}
            {owner && (
              <Card title={
                <span className="flex items-center gap-2">
                  <Icon name="user" className="w-4 h-4" />
                  所有者情報
                  <Badge variant="info" size="sm">
                    {classificationLabel(owner.owner_classification)}
                  </Badge>
                </span>
              }>
                <PartyTable
                  fullnameValue={owner.owner_fullname || owner.owner_corporation_name}
                  furigana={owner.owner_furigana}
                  address={owner.owner_address}
                  tel={owner.owner_tel}
                  email={owner.owner_email_address}
                  extras={[
                    owner.owner_birthday && { label: '生年月日', value: owner.owner_birthday },
                    owner.owner_corporation_representative_name && { label: '代表者名', value: owner.owner_corporation_representative_name },
                    owner.owner_department_name && { label: '部署名', value: owner.owner_department_name },
                  ].filter(Boolean)}
                  onRowClick={() => setSelectedParty({ kind: 'owner', raw: owner })}
                />
              </Card>
            )}

            {/* 使用者情報（所有者と異なる場合のみ） */}
            {isUserDifferent && (
              <Card title={
                <span className="flex items-center gap-2">
                  <Icon name="users" className="w-4 h-4" />
                  使用者情報
                  <Badge variant="info" size="sm">
                    {classificationLabel(user.user_classification)}
                  </Badge>
                </span>
              }>
                <PartyTable
                  fullnameValue={user.user_fullname || user.user_corporation_name}
                  furigana={user.user_furigana}
                  address={user.user_address}
                  tel={user.user_tel}
                  email={user.user_email_address}
                  extras={[
                    user.user_corporation_representative_name && { label: '代表者名', value: user.user_corporation_representative_name },
                    user.user_department_name && { label: '部署名', value: user.user_department_name },
                  ].filter(Boolean)}
                  onRowClick={() => setSelectedParty({ kind: 'user', raw: user })}
                />
              </Card>
            )}
          </div>
        );
      })()}

      {/* 機体一覧 */}
      {dipsAuthenticated && !dipsLoading && (() => {
        // 有効な機体を先に、無効な機体を後に表示
        const sortedAircrafts = dipsAircrafts ? [...dipsAircrafts].sort((a, b) => {
          const statusA = getAircraftInfo(a)?.aircraft_status;
          const statusB = getAircraftInfo(b)?.aircraft_status;
          // '1'（有効）を先に
          if (statusA === '1' && statusB !== '1') return -1;
          if (statusA !== '1' && statusB === '1') return 1;
          return 0;
        }) : [];

        const activeCount = sortedAircrafts.filter(a => getAircraftInfo(a)?.aircraft_status === '1').length;
        const inactiveCount = sortedAircrafts.length - activeCount;

        return (
          <Card
            title="登録機体一覧"
            headerActions={
              <div className="flex items-center gap-3 text-sm">
                <span className="text-text-secondary">
                  {sortedAircrafts.length}機
                </span>
                {activeCount > 0 && (
                  <Badge variant="success" size="sm">有効 {activeCount}</Badge>
                )}
                {inactiveCount > 0 && (
                  <Badge variant="default" size="sm">無効 {inactiveCount}</Badge>
                )}
              </div>
            }
          >
            {sortedAircrafts.length > 0 ? (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border-light text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
                      <th className="py-3 px-3 font-semibold">状態</th>
                      <th className="py-3 px-3 font-semibold">登録記号</th>
                      <th className="py-3 px-3 font-semibold">メーカー / モデル</th>
                      <th className="py-3 px-3 font-semibold">機体種類</th>
                      <th className="py-3 px-3 font-semibold text-right">重量 (kg)</th>
                      <th className="py-3 px-3 font-semibold">有効期限</th>
                      <th className="py-3 px-3 font-semibold text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAircrafts.map((aircraft, idx) => {
                      const info = getAircraftInfo(aircraft);
                      const isActive = info.aircraft_status === '1';
                      return (
                        <tr
                          key={idx}
                          onClick={() => setSelectedAircraft(aircraft)}
                          style={{ animationDelay: `${idx * 30}ms` }}
                          className={`nexus-row-slide-in border-b border-border-light cursor-pointer transition-colors hover:bg-light-neutral ${
                            isActive ? '' : 'opacity-60 hover:opacity-100'
                          }`}
                        >
                          <td className="py-3 px-3">
                            <Badge
                              variant={isActive ? 'success' : info.aircraft_status === '2' ? 'warning' : 'default'}
                              size="sm"
                            >
                              {isActive ? '有効' :
                               info.aircraft_status === '2' ? '期限切れ' : '抹消済'}
                            </Badge>
                          </td>
                          <td className="py-3 px-3">
                            <div className="font-mono font-semibold text-nexus-primary">
                              {info.registration_code || '—'}
                            </div>
                            {info.manufacturing_number && (
                              <div className="text-xs text-text-secondary mt-0.5 font-mono">
                                #{info.manufacturing_number}
                              </div>
                            )}
                          </td>
                          <td className="py-3 px-3">
                            <div className="text-text-primary font-medium">
                              {info.manufacturer_jpn || info.manufacturer_eng || '—'}
                            </div>
                            <div className="text-xs text-text-secondary mt-0.5">
                              {info.model_jpn || info.model_eng || '—'}
                            </div>
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center gap-2 text-text-primary">
                              <span className="text-nexus-primary flex-shrink-0">
                                <AircraftTypeIcon type={info.aircraft_type} size={20} />
                              </span>
                              {AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type || '不明'}
                            </div>
                          </td>
                          <td className="py-3 px-3 text-right text-text-primary font-mono">
                            {info.aircraft_weight ?? '—'}
                            {info.maximum_takeoff_weight && (
                              <span className="text-text-secondary"> / {info.maximum_takeoff_weight}</span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-text-primary font-mono">
                            {formatDate(info.effectiveness_period_to)}
                          </td>
                          <td className="py-3 px-3">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowFlightPlans(aircraft);
                                }}
                                title="飛行計画"
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-nexus-primary hover:bg-nexus-blue-soft transition-colors"
                              >
                                <MaterialIcon name="calendar_month" className="text-[16px]" />
                                飛行計画
                              </button>
                              <span className="text-outline">
                                <MaterialIcon name="chevron_right" className="text-[18px]" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-text-secondary">
                <MaterialIcon name="flight" className="text-[48px] text-outline-variant mb-3" />
                <p>DIPS2.0に登録されている機体はありません</p>
              </div>
            )}
          </Card>
        );
      })()}

      {/* 飛行計画セクション */}
      {dipsAuthenticated && !dipsLoading && (
        <Card
          title={
            <span className="flex items-center gap-2">
              <Icon name="calendar" className="w-4 h-4" />
              飛行計画
              {flightPlanAuthenticated ? (
                <Badge variant="success" size="sm">認証済み</Badge>
              ) : (
                <Badge variant="default" size="sm">要認証</Badge>
              )}
            </span>
          }
          headerActions={
            <div className="flex items-center gap-2">
              {flightPlanAuthenticated && (
                <>
                  <input
                    type="date"
                    value={searchDate}
                    onChange={(e) => setSearchDate(e.target.value)}
                    className="px-2 py-1 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-nexus-primary"
                    disabled={scanMode}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => { setScanResults([]); fetchFlightPlans(); }}
                    disabled={flightPlansLoading || scanMode}
                  >
                    <Icon name="refresh" className="w-3 h-3" />
                    検索
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={scanDateRange}
                    disabled={flightPlansLoading || scanMode}
                  >
                    <Icon name="search" className="w-3 h-3" />
                    {scanMode ? scanProgress : '範囲スキャン'}
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => setShowReportForm(!showReportForm)}
                  >
                    <Icon name="plus" className="w-3 h-3" />
                    飛行計画通報
                  </Button>
                </>
              )}
              {!flightPlanAuthenticated && (
                <Button
                  size="sm"
                  onClick={handleFlightPlanAuth}
                  disabled={flightPlanAuthLoading}
                >
                  <Icon name="link" className="w-3 h-3" />
                  {flightPlanAuthLoading ? '認証中...' : '飛行計画認証'}
                </Button>
              )}
            </div>
          }
        >
          {/* 飛行計画通報フォーム */}
          {showReportForm && flightPlanAuthenticated && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="text-sm font-medium text-text-primary mb-4 flex items-center gap-2">
                <Icon name="plus" className="w-4 h-4" />
                飛行計画通報（新規登録）
              </h4>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* 飛行日 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">飛行日</label>
                  <input
                    type="date"
                    value={reportForm.flightDate}
                    onChange={(e) => setReportForm({ ...reportForm, flightDate: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 開始時刻 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">開始時刻</label>
                  <input
                    type="time"
                    value={reportForm.startTime}
                    onChange={(e) => setReportForm({ ...reportForm, startTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 終了時刻 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">終了時刻</label>
                  <input
                    type="time"
                    value={reportForm.endTime}
                    onChange={(e) => setReportForm({ ...reportForm, endTime: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 緯度 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">緯度</label>
                  <input
                    type="text"
                    value={reportForm.latitude}
                    onChange={(e) => setReportForm({ ...reportForm, latitude: e.target.value })}
                    placeholder="例: 35.6895"
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 経度 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">経度</label>
                  <input
                    type="text"
                    value={reportForm.longitude}
                    onChange={(e) => setReportForm({ ...reportForm, longitude: e.target.value })}
                    placeholder="例: 139.6917"
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 半径 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">半径 (m)</label>
                  <input
                    type="number"
                    value={reportForm.radius}
                    onChange={(e) => setReportForm({ ...reportForm, radius: e.target.value })}
                    placeholder="例: 500"
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 飛行目的 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">飛行目的</label>
                  <select
                    value={reportForm.purpose}
                    onChange={(e) => setReportForm({ ...reportForm, purpose: e.target.value })}
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="空撮">空撮</option>
                    <option value="測量">測量</option>
                    <option value="点検">点検</option>
                    <option value="農薬散布">農薬散布</option>
                    <option value="物資輸送">物資輸送</option>
                    <option value="趣味">趣味</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                {/* 最大高度 */}
                <div>
                  <label className="block text-xs text-text-secondary mb-1">最大高度 (m)</label>
                  <input
                    type="number"
                    value={reportForm.maxAltitude}
                    onChange={(e) => setReportForm({ ...reportForm, maxAltitude: e.target.value })}
                    placeholder="例: 150"
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                {/* 備考 */}
                <div className="md:col-span-2 lg:col-span-1">
                  <label className="block text-xs text-text-secondary mb-1">備考</label>
                  <input
                    type="text"
                    value={reportForm.remarks}
                    onChange={(e) => setReportForm({ ...reportForm, remarks: e.target.value })}
                    placeholder="任意"
                    className="w-full px-3 py-2 text-sm border border-border-light rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              {/* 送信ボタン */}
              <div className="mt-4 flex items-center gap-3">
                <Button
                  variant="success"
                  onClick={handleReportFlightPlan}
                  disabled={reportLoading}
                >
                  <Icon name="send" className="w-4 h-4" />
                  {reportLoading ? '送信中...' : '飛行計画を通報'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowReportForm(false)}
                >
                  キャンセル
                </Button>
              </div>

              {/* 結果表示 */}
              {reportResult && (
                <div className="mt-4">
                  {reportResult.success ? (
                    <Alert variant="success">
                      飛行計画を通報しました
                      <pre className="mt-2 text-xs bg-white p-2 rounded overflow-auto max-h-32">
                        {JSON.stringify(reportResult.data, null, 2)}
                      </pre>
                    </Alert>
                  ) : (
                    <Alert variant="error">
                      エラー: {reportResult.error}
                    </Alert>
                  )}
                </div>
              )}
            </div>
          )}

          {!flightPlanAuthenticated ? (
            <div className="text-center py-8 text-text-secondary">
              <Icon name="lock" className="w-10 h-10 mx-auto mb-3 text-outline-variant" />
              <p>飛行計画の表示には別途認証が必要です</p>
              <p className="text-sm mt-1">右上の「飛行計画認証」ボタンを押してください</p>
            </div>
          ) : scanMode ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-nexus-primary border-t-transparent rounded-full animate-spin mb-3" />
              <span className="text-text-primary font-medium">{scanProgress}</span>
              <span className="text-sm text-text-secondary mt-1">過去2週間〜未来2ヶ月をスキャン中...</span>
            </div>
          ) : flightPlansLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-3 border-nexus-primary border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-text-secondary">飛行計画を取得中...</span>
            </div>
          ) : flightPlansError ? (
            <div className="py-8 text-center">
              <Alert variant="warning">
                飛行計画の取得に失敗しました: {flightPlansError}
              </Alert>
              <p className="text-sm text-text-secondary mt-2">
                飛行計画APIには別途認証が必要な場合があります
              </p>
            </div>
          ) : scanResults.length > 0 ? (
            // スキャン結果表示
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-text-secondary pb-2 border-b">
                <Icon name="check" className="w-4 h-4 text-green-500" />
                スキャン完了: {scanResults.reduce((sum, r) => sum + r.count, 0)}件の飛行計画を{scanResults.length}日で発見
              </div>
              <div className="space-y-2">
                {scanResults.map((result, idx) => (
                  <div
                    key={idx}
                    className="p-3 bg-amber-50 border border-amber-200 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-medium text-text-primary">
                        {result.date}
                      </div>
                      <Badge variant="warning" size="sm">
                        {result.count}件
                      </Badge>
                    </div>
                    {/* 各日の飛行計画リスト */}
                    <div className="space-y-1">
                      {result.plans.map((plan, pIdx) => (
                        <div
                          key={pIdx}
                          className="text-sm p-2 bg-white rounded border border-amber-100 hover:bg-amber-50 cursor-pointer"
                          onClick={() => setSelectedFlightPlan(plan)}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-text-primary">
                              {plan.flight_purpose || plan.purpose || plan.flightPurpose || '飛行計画'}
                            </span>
                            <Icon name="chevronRight" className="w-3 h-3 text-outline" />
                          </div>
                          {(plan.flight_area || plan.flightArea) && (
                            <div className="text-xs text-text-secondary mt-1">
                              <Icon name="mapPin" className="w-3 h-3 inline mr-1" />
                              {plan.flight_area || plan.flightArea}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : flightPlans.length > 0 ? (
            // 単日検索結果表示
            <div className="space-y-2">
              {flightPlans.map((plan, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors"
                  onClick={() => setSelectedFlightPlan(plan)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-text-primary">
                        {plan.flight_purpose || plan.purpose || '飛行計画'}
                      </div>
                      <div className="text-sm text-text-secondary mt-1">
                        {formatDate(plan.flight_start_datetime || plan.start_date)} ～ {formatDate(plan.flight_end_datetime || plan.end_date)}
                      </div>
                      {plan.flight_area && (
                        <div className="text-sm text-text-secondary mt-1">
                          <Icon name="mapPin" className="w-3 h-3 inline mr-1" />
                          {plan.flight_area}
                        </div>
                      )}
                    </div>
                    <Badge variant="warning" size="sm">
                      {plan.status || '通報済み'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-text-secondary">
              <Icon name="calendar" className="w-10 h-10 mx-auto mb-3 text-outline-variant" />
              <p>{searchDate} の飛行計画はありません</p>
              <p className="text-xs mt-2">「過去2週間〜未来2ヶ月をスキャン」ボタンで広範囲を検索できます</p>
            </div>
          )}
        </Card>
      )}

      {/* 飛行計画詳細モーダル */}
      <Modal
        isOpen={!!selectedFlightPlan}
        onClose={() => setSelectedFlightPlan(null)}
        title="飛行計画詳細"
        size="lg"
      >
        {selectedFlightPlan && (
          <div className="space-y-4">
            <div className="bg-light-neutral rounded-lg p-4 max-h-96 overflow-auto">
              <pre className="text-xs text-text-secondary whitespace-pre-wrap">
                {JSON.stringify(selectedFlightPlan, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>

      {/* 機体詳細ドロワー */}
      <Drawer
        isOpen={!!selectedAircraft}
        onClose={() => setSelectedAircraft(null)}
        title={`機体詳細: ${getAircraftInfo(selectedAircraft)?.registration_code || ''}`}
        width="640px"
      >
        {selectedAircraft && (() => {
          const info = getAircraftInfo(selectedAircraft);
          return (
            <>
              <TabBar value={aircraftDetailTab} onChange={setAircraftDetailTab} tabs={AIRCRAFT_DETAIL_TABS} />
              <div className="p-6">
                {aircraftDetailTab === 'basic' && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <DetailRow label="登録記号" value={info.registration_code} />
                    <DetailRow label="製造番号" value={info.manufacturing_number} />
                    <DetailRow label="機体種類" value={AIRCRAFT_TYPE_LABELS[info.aircraft_type] || info.aircraft_type} />
                    <DetailRow label="メーカー" value={info.manufacturer_jpn || info.manufacturer_eng} />
                    <DetailRow label="モデル" value={info.model_jpn || info.model_eng} />
                    <DetailRow label="登録日" value={formatDate(info.effectiveness_period_self)} />
                    <DetailRow label="有効期限" value={formatDate(info.effectiveness_period_to)} />
                    <DetailRow label="最終更新日" value={formatDate(info.last_update_date)} />
                    <DetailRow
                      label="ステータス"
                      value={
                        <Badge
                          variant={info.aircraft_status === '1' ? 'success' : info.aircraft_status === '2' ? 'warning' : 'default'}
                          size="sm"
                        >
                          {info.aircraft_status === '1' ? '有効' :
                           info.aircraft_status === '2' ? '期限切れ' : '抹消済'}
                        </Badge>
                      }
                    />
                  </div>
                )}

                {aircraftDetailTab === 'rid' && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <DetailRow label="RID製造番号" value={info.rid_manufacturing_number} />
                    <DetailRow label="RIDメーカー" value={info.rid_manufacturer_jpn || info.rid_manufacturer_eng} />
                    <DetailRow label="RID型式" value={info.rid_model_jpn || info.rid_model_eng} />
                    <DetailRow label="RID種類" value={info.rid_type === '1' ? '内蔵型' : info.rid_type === '2' ? '外付け型' : info.rid_type} />
                  </div>
                )}

                {aircraftDetailTab === 'spec' && (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <DetailRow label="機体重量" value={info.aircraft_weight ? `${info.aircraft_weight} kg` : '—'} />
                    <DetailRow label="最大離陸重量" value={info.maximum_takeoff_weight ? `${info.maximum_takeoff_weight} kg` : '—'} />
                    <DetailRow label="全長" value={info.aircraft_length ? `${info.aircraft_length} m` : '—'} />
                    <DetailRow label="全幅" value={info.aircraft_width ? `${info.aircraft_width} m` : '—'} />
                    <DetailRow label="全高" value={info.aircraft_height ? `${info.aircraft_height} m` : '—'} />
                  </div>
                )}

                {aircraftDetailTab === 'raw' && (
                  <div className="bg-light-neutral rounded-lg p-4 overflow-auto">
                    <pre className="text-xs text-text-secondary whitespace-pre-wrap font-mono">
                      {JSON.stringify(selectedAircraft, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </Drawer>

      {/* 所有者・使用者 詳細ドロワー */}
      <Drawer
        isOpen={!!selectedParty}
        onClose={() => setSelectedParty(null)}
        title={selectedParty?.kind === 'owner' ? '所有者情報' : '使用者情報'}
        width="640px"
      >
        {selectedParty && (() => {
          const p = selectedParty.raw;
          const isOwner = selectedParty.kind === 'owner';
          const classification = isOwner ? p.owner_classification : p.user_classification;
          const detailRows = isOwner
            ? [
                ['区分', classificationLabel(classification)],
                ['氏名/法人名', p.owner_fullname || p.owner_corporation_name],
                ['フリガナ', p.owner_furigana],
                ['住所', p.owner_address],
                ['電話番号', p.owner_tel],
                ['メールアドレス', p.owner_email_address],
                ['生年月日', p.owner_birthday],
                ['代表者名', p.owner_corporation_representative_name],
                ['部署名', p.owner_department_name],
              ]
            : [
                ['区分', classificationLabel(classification)],
                ['氏名/法人名', p.user_fullname || p.user_corporation_name],
                ['フリガナ', p.user_furigana],
                ['住所', p.user_address],
                ['電話番号', p.user_tel],
                ['メールアドレス', p.user_email_address],
                ['代表者名', p.user_corporation_representative_name],
                ['部署名', p.user_department_name],
              ];
          return (
            <>
              <TabBar value={partyDetailTab} onChange={setPartyDetailTab} tabs={PARTY_DETAIL_TABS} />
              <div className="p-6">
                {partyDetailTab === 'detail' && (
                  <div className="grid grid-cols-1 gap-3 text-sm">
                    {detailRows.map(([label, value]) => (
                      <DetailRow key={label} label={label} value={value} />
                    ))}
                  </div>
                )}
                {partyDetailTab === 'raw' && (
                  <div className="bg-light-neutral rounded-lg p-4 overflow-auto">
                    <pre className="text-xs text-text-secondary whitespace-pre-wrap font-mono">
                      {JSON.stringify(p, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          );
        })()}
      </Drawer>
    </div>
  );
}

// 詳細行コンポーネント
function DetailRow({ label, value }) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-outline w-32 flex-shrink-0">{label}:</span>
      <span className="text-text-primary flex-1 min-w-0 break-all">{value || '—'}</span>
    </div>
  );
}

// 所有者・使用者情報のテーブル (機体一覧と同じスタイル、1行のみ)
function PartyTable({ fullnameValue, furigana, address, tel, email, extras = [], onRowClick }) {
  return (
    <div className="overflow-x-auto -mx-6 px-6">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border-light text-left text-xs font-semibold text-text-secondary uppercase tracking-wider">
            <th className="py-3 px-3 font-semibold">氏名 / 法人名</th>
            <th className="py-3 px-3 font-semibold">フリガナ</th>
            <th className="py-3 px-3 font-semibold">住所</th>
            <th className="py-3 px-3 font-semibold">電話</th>
            <th className="py-3 px-3 font-semibold">メール</th>
            {extras.map((e) => (
              <th key={e.label} className="py-3 px-3 font-semibold">{e.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr
            onClick={onRowClick}
            className="nexus-row-slide-in border-b border-border-light cursor-pointer transition-colors hover:bg-light-neutral"
          >
            <td className="py-3 px-3 text-text-primary font-medium">{fullnameValue || '—'}</td>
            <td className="py-3 px-3 text-text-secondary">{furigana || '—'}</td>
            <td className="py-3 px-3 text-text-primary">{address || '—'}</td>
            <td className="py-3 px-3 text-text-primary font-mono">{tel || '—'}</td>
            <td className="py-3 px-3 text-text-primary">{email || '—'}</td>
            {extras.map((e) => (
              <td key={e.label} className="py-3 px-3 text-text-primary">{e.value || '—'}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
